import {
  DEFAULT_VALUE_INPUT_MODE,
  Dimension,
  DimensionFilterConstraint,
  DimensionFilterValue,
  DimensionFilterValueType,
  DimensionValueInputMode,
  DraftDimensionFilterConstraint,
} from '../constants';

type DimensionFilterConstraintLike =
  | DimensionFilterConstraint
  | DraftDimensionFilterConstraint;

export const getDimensionValueInputMode = (
  dimension?: Pick<Dimension, 'value_input_mode'> | null,
): DimensionValueInputMode => {
  return dimension?.value_input_mode ?? DEFAULT_VALUE_INPUT_MODE;
};

export const getDimensionFilterValueType = (
  dimension?: Pick<Dimension, 'data_type'> | null,
): DimensionFilterValueType | undefined => {
  return dimension?.data_type === 'UNKNOWN' ? undefined : dimension?.data_type;
};

export const isDimensionFilterValueEmpty = (
  values: DimensionFilterConstraintLike['values'],
): boolean => {
  return (
    values === null ||
    values === undefined ||
    values === '' ||
    (Array.isArray(values) && values.length === 0)
  );
};

export const isFreeTextFilterPendingValidation = (
  constraint: DimensionFilterConstraintLike,
): boolean => {
  return (
    constraint.value_input_mode === 'FREE_TEXT' &&
    !constraint.validation_status &&
    !isDimensionFilterValueEmpty(constraint.values)
  );
};

export const normalizeFreeTextFilterValues = (
  values: DimensionFilterValue | DimensionFilterValue[] | null | undefined,
): string[] => {
  if (values === null || values === undefined) {
    return [];
  }

  const rawValues = Array.isArray(values) ? values : [values];
  const seenValues = new Set<string>();

  return rawValues.reduce<string[]>((result, value) => {
    const normalizedValue = String(value).trim();
    if (!normalizedValue || seenValues.has(normalizedValue)) {
      return result;
    }

    seenValues.add(normalizedValue);
    result.push(normalizedValue);
    return result;
  }, []);
};

const parseBooleanValue = (value: string): boolean | null => {
  const normalizedValue = value.trim().toLowerCase();
  if (normalizedValue === 'true') return true;
  if (normalizedValue === 'false') return false;
  return null;
};

export type BuildFreeTextDimensionFilterValueResult = {
  type: DimensionFilterValueType;
  values: DimensionFilterValue[];
  removedValues?: string[];
};

export const buildFreeTextDimensionFilterValue = (
  values: DimensionFilterValue | DimensionFilterValue[] | null | undefined,
  detectedType?: DimensionFilterValueType | null,
): BuildFreeTextDimensionFilterValueResult => {
  const normalizedValues = normalizeFreeTextFilterValues(values);
  const type = detectedType ?? 'STRING';

  if (type === 'INTEGER') {
    const validValues: number[] = [];
    const removedValues: string[] = [];

    for (const value of normalizedValues) {
      const num = Number(value);
      if (Number.isInteger(num)) {
        validValues.push(num);
      } else {
        removedValues.push(value);
      }
    }

    return {
      type,
      values: validValues,
      removedValues: removedValues.length ? removedValues : undefined,
    };
  }

  if (type === 'BOOLEAN') {
    const validValues: boolean[] = [];
    const removedValues: string[] = [];

    for (const value of normalizedValues) {
      const bool = parseBooleanValue(value);
      if (bool !== null) {
        validValues.push(bool);
      } else {
        removedValues.push(value);
      }
    }

    return {
      type,
      values: validValues,
      removedValues: removedValues.length ? removedValues : undefined,
    };
  }

  return {
    type: 'STRING',
    values: normalizedValues,
  };
};
